<template>
  <div
    class="relative flex flex-col gap-5 px-7 pt-[calc(env(safe-area-inset-top)+2rem)] pb-[calc(env(safe-area-inset-bottom)+1.75rem)]">
    <!-- Header block -->
    <div>
      <span class="text-[11px] font-semibold uppercase tracking-[0.22em] text-primary-600/80">
        {{ t('feedback.modal.unhappy.eyebrow') }}
      </span>
      <i18n-t
        keypath="feedback.modal.unhappy.title"
        tag="h2"
        class="mt-2 font-bold text-neutral-900 leading-[1.1] tracking-tight text-[26px] max-w-[22ch]"
      >
        <template #emphasis>
          <span class="font-normal text-primary-600">{{ t('feedback.modal.unhappy.title_emphasis') }}</span>
        </template>
      </i18n-t>
      <p class="mt-2 text-[14px] leading-relaxed text-neutral-600 max-w-[34ch]">
        {{ t('feedback.modal.unhappy.subtitle') }}
      </p>
    </div>

    <!-- Criteria block -->
    <div class="flex flex-col gap-3">
      <CriterionRater
        v-model="ratings.usability"
        :label="t('feedback.modal.unhappy.criterion.usability')"
      />
      <CriterionRater
        v-model="ratings.design"
        :label="t('feedback.modal.unhappy.criterion.design')"
      />
      <CriterionRater
        v-model="ratings.features"
        :label="t('feedback.modal.unhappy.criterion.features')"
      />
      <CriterionRater
        v-model="ratings.utility"
        :label="t('feedback.modal.unhappy.criterion.utility')"
      />
    </div>

    <!-- Free text. Email is taken from the auth user and sent in the payload
         but kept hidden from the form to reduce friction. -->
    <div class="flex flex-col gap-3">
      <div class="editorial-field">
        <textarea
          v-model="message"
          :placeholder="t('feedback.modal.unhappy.message.placeholder')"
          :maxlength="MESSAGE_MAX"
          rows="3"
          class="editorial-textarea"
        />
        <p class="text-right text-[11px] text-neutral-400 tabular-nums">
          {{ message.length }} / {{ MESSAGE_MAX }}
        </p>
      </div>
    </div>

    <!-- Actions -->
    <div class="flex gap-3 mt-1">
      <button
        type="button"
        class="action-btn action-secondary flex-1"
        :disabled="submitting"
        @click="$emit('cancel')"
      >
        {{ t('feedback.modal.unhappy.cancel') }}
      </button>
      <button
        type="button"
        class="action-btn action-primary flex-[1.4]"
        :disabled="submitting || !canSubmit"
        @click="handleSubmit"
      >
        <span v-if="submitting" class="action-spinner" aria-hidden="true" />
        <span>{{ submitting ? '…' : t('feedback.modal.unhappy.submit') }}</span>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { toast } from 'vue-sonner';
import { Capacitor } from '@capacitor/core';
import CriterionRater from './CriterionRater.vue';
import { feedbackService, type CriterionRating } from '@/services/feedback.service';
import { getAppVersion } from '@/utils/appVersion';
import { useAuthStore } from '@/stores/auth';
import { useAnalytics } from '@/composables/useAnalytics';

const props = defineProps<{
  /**
   * Texte déposé dans le champ message à l'ouverture, quand l'appelant sait
   * quelque chose que le parent ne peut pas savoir. Aujourd'hui : le contexte
   * technique d'un bug déclaré au paywall (écran, version, plateforme). Le
   * parent écrit à la suite et reste libre de tout effacer.
   */
  initialMessage?: string;
}>();

const emit = defineEmits<{
  (e: 'submitted'): void;
  (e: 'cancel'): void;
}>();

const { t, locale } = useI18n();
const authStore = useAuthStore();
const { logEvent } = useAnalytics();

const ratings = reactive({
  usability: null as CriterionRating,
  design: null as CriterionRating,
  features: null as CriterionRating,
  utility: null as CriterionRating,
});
/** Plafond du champ libre, repris de l'ancien :maxlength d'ion-textarea. */
const MESSAGE_MAX = 1000;
const message = ref(props.initialMessage ?? '');
const email = ref(authStore.user?.email ?? '');
const submitting = ref(false);
const appVersion = ref('unknown');

// All four criteria must be rated; message stays optional. Email is
// pre-filled from auth but still must be syntactically valid.
const allRated = computed(
  () => !!(ratings.usability && ratings.design && ratings.features && ratings.utility)
);
const hasValidEmail = computed(() => /\S+@\S+\.\S+/.test(email.value.trim()));
const canSubmit = computed(() => allRated.value && hasValidEmail.value);

onMounted(async () => {
  appVersion.value = await getAppVersion();
});

/**
 * L'ion-modal hôte garde ce composant monté d'une ouverture à l'autre selon la
 * version d'Ionic : sans ce watcher, le pré-remplissage n'arriverait que la
 * toute première fois, et le contexte technique manquerait à tous les messages
 * suivants. `?? ''` remet à zéro quand on rouvre depuis les réglages.
 */
watch(() => props.initialMessage, (value) => {
  message.value = value ?? '';
});

function detectPlatform(): 'ios' | 'android' | 'web' {
  if (!Capacitor.isNativePlatform()) return 'web';
  return Capacitor.getPlatform() === 'android' ? 'android' : 'ios';
}

async function handleSubmit() {
  // Defensive guard — the button is disabled but a stale enter-key press
  // could still race here.
  if (!canSubmit.value || submitting.value) return;
  submitting.value = true;
  const result = await feedbackService.submitNegativeFeedback({
    email: email.value,
    ratings: {
      usability: ratings.usability,
      design: ratings.design,
      features: ratings.features,
      utility: ratings.utility,
    },
    message: message.value || null,
    app_version: appVersion.value,
    platform: detectPlatform(),
    locale: (locale.value === 'en' ? 'en' : 'fr'),
  });
  submitting.value = false;

  if (result.ok) {
    logEvent('feedback_form_submitted', {
      ratings: {
        usability: ratings.usability,
        design: ratings.design,
        features: ratings.features,
        utility: ratings.utility,
      },
      has_message: !!message.value,
    });
    toast.success(t('feedback.toast.sent_success'), { duration: 2500 });
    emit('submitted');
  } else {
    logEvent('feedback_form_submit_failed', {
      error_code: result.error ?? 'unknown',
    });
    const key = result.error === 'rate_limit'
      ? 'feedback.toast.rate_limited'
      : 'feedback.toast.sent_error';
    toast.error(t(key), { duration: 3000 });
  }
}
</script>

<style scoped>
.editorial-field {
  position: relative;
  border-radius: 16px;
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.06);
  padding: 4px 10px;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.editorial-field:focus-within {
  border-color: var(--color-primary-500);
  background: white;
  box-shadow: 0 0 0 4px rgba(255, 127, 110, 0.12);
}

.editorial-field.compact {
  padding: 0 10px;
}

/* Champ libre : la carte porte déjà le cadre, le textarea doit disparaître
   dedans (pas de bordure, fond transparent). auto-grow d'ion-textarea est
   remplacé par un redimensionnement vertical natif. */
.editorial-textarea {
  width: 100%;
  padding: 6px 0;
  border: none;
  background: transparent;
  color: var(--color-neutral-900);
  font-size: 14px;
  font-family: inherit;
  line-height: 1.5;
  resize: vertical;
  outline: none;
}

.editorial-textarea::placeholder {
  color: var(--color-neutral-400);
  opacity: 1;
}

.action-btn {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 13px 18px;
  border-radius: 999px;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: -0.01em;
  border: none;
  cursor: pointer;
  transition: transform 0.2s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.2s ease, background 0.2s ease;
}

.action-btn:disabled {
  opacity: 0.55;
  cursor: wait;
}

.action-secondary {
  background: #ffffff;
  color: var(--color-neutral-700);
  border: 1px solid rgba(0, 0, 0, 0.08);
}

.action-secondary:hover:not(:disabled) {
  color: var(--color-neutral-900);
  border-color: rgba(0, 0, 0, 0.16);
}

.action-primary {
  background: linear-gradient(135deg, var(--color-primary-500) 0%, var(--color-primary-600) 100%);
  color: white;
  box-shadow:
    0 10px 20px -8px rgba(232, 90, 77, 0.5),
    inset 0 1px 0 rgba(255, 255, 255, 0.22);
}

.action-primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow:
    0 14px 26px -8px rgba(232, 90, 77, 0.6),
    inset 0 1px 0 rgba(255, 255, 255, 0.28);
}

.action-primary:active:not(:disabled) {
  transform: translateY(0) scale(0.98);
}

.action-spinner {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.35);
  border-top-color: white;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}
</style>
